import { BaseService } from './BaseService';
import { PartidoFilters, ApiResponse, Partido, UpdatePartidoDto } from '@/types';

/**
 * Servicio para gestionar partidos
 */
export class PartidoService extends BaseService {
  constructor() {
    super('/partidos');
  }

  /**
   * Obtiene los partidos asignados a un entrenador
   * @param entrenadorId - ID del entrenador
   * @param filters - Filtros opcionales
   * @returns Promise con los partidos del entrenador
   */
  async getPartidosEntrenador(
    entrenadorId: number,
    filters?: PartidoFilters
  ): Promise<ApiResponse<Partido[]>> {
    const params = new URLSearchParams();
    
    if (filters?.fechaInicio) {
      params.append('fechaInicio', filters.fechaInicio);
    }
    if (filters?.fechaFin) {
      params.append('fechaFin', filters.fechaFin);
    }
    if (filters?.categoria) {
      params.append('categoria', filters.categoria);
    }
    if (filters?.competicion) {
      params.append('competicion', filters.competicion);
    }

    const queryString = params.toString();
    const url = `${this.baseURL}${this.endpoint}/entrenador/${entrenadorId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    return this.handleResponse<ApiResponse<Partido[]>>(response);
  }

  /**
   * Obtiene los partidos del fin de semana actual
   * @param entrenadorId - ID del entrenador
   * @returns Promise con los partidos del fin de semana
   */
  async getPartidosFinDeSemana(entrenadorId: number): Promise<ApiResponse<Partido[]>> {
    const { fechaInicio, fechaFin } = this.getFinDeSemanaActual();
    
    return this.getPartidosEntrenador(entrenadorId, {
      fechaInicio,
      fechaFin
    });
  }

  /**
   * Calcula las fechas del fin de semana actual (sábado y domingo)
   * @returns Objeto con fechas de inicio y fin del fin de semana
   */
  private getFinDeSemanaActual(): { fechaInicio: string; fechaFin: string } {
    const hoy = new Date();
    const diaSemana = hoy.getDay(); // 0 = domingo, 1 = lunes, ..., 6 = sábado
    
    // Calcular el sábado de esta semana
    const diasHastaSabado = 6 - diaSemana;
    const sabado = new Date(hoy);
    sabado.setDate(hoy.getDate() + diasHastaSabado);
    sabado.setHours(0, 0, 0, 0);
    
    // Calcular el domingo de esta semana
    const domingo = new Date(sabado);
    domingo.setDate(sabado.getDate() + 1);
    domingo.setHours(23, 59, 59, 999);
    
    return {
      fechaInicio: sabado.toISOString(),
      fechaFin: domingo.toISOString()
    };
  }

  /**
   * Obtiene los partidos asignados a un entrenador optimizado para listados (sin foto)
   * @param entrenadorId - ID del entrenador
   * @param filters - Filtros opcionales
   * @returns Promise con los partidos del entrenador
   */
  async getPartidosEntrenadorOptimizado(
    entrenadorId: number,
    filters?: PartidoFilters
  ): Promise<ApiResponse<Partido[]>> {
    const params = new URLSearchParams();
    
    if (filters?.fechaInicio) {
      params.append('fechaInicio', filters.fechaInicio);
    }
    if (filters?.fechaFin) {
      params.append('fechaFin', filters.fechaFin);
    }
    if (filters?.categoria) {
      params.append('categoria', filters.categoria);
    }
    if (filters?.competicion) {
      params.append('competicion', filters.competicion);
    }

    const queryString = params.toString();
    const url = `${this.baseURL}${this.endpoint}/entrenador/${entrenadorId}/list${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    return this.handleResponse<ApiResponse<Partido[]>>(response);
  }

  /**
   * Obtiene la foto de un partido específico
   */
  async obtenerFotoPartido(id: number): Promise<ApiResponse<string>> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/${id}/foto`, {
      method: 'GET',
      headers: this.getHeaders()
    });
    
    return this.handleResponse<ApiResponse<string>>(response);
  }

  /**
   * Actualiza la información adicional de un partido
   * @param id ID del partido
   * @param updateDto Datos a actualizar
   * @returns Promise con el partido actualizado
   */
  async actualizarInformacionAdicional(
    id: number,
    updateDto: UpdatePartidoDto,
    onUploadProgress?: (percentage: number) => void
  ): Promise<ApiResponse<Partido>> {
    return this.putWithUploadProgress<Partido>(
      `${id}/informacion-adicional`,
      updateDto,
      onUploadProgress
    );
  }
}

export const partidoService = new PartidoService();
